import { useState, useMemo } from 'react'
import { SimpleChart } from '@/components/SimpleChart'
import { ErrorState, LoadingSkeleton } from '@/components/States'
import { useAnalytics, useDashboard, useDocuments } from '@/hooks/useRootQueries'
import { StatCard } from '@/components/StatCard'
import { IconDocuments, IconSignatures, IconActivity, IconOrganizations } from '@/components/Icons'
import type { AnalyticsPoint, CategoryStat } from '@/types/api'

type Range = '24h' | '7d' | '30d' | '90d' | 'all'
const RANGES: { value: Range; label: string }[] = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'all', label: 'All Time' },
]

export function Analytics() {
  const [range, setRange] = useState<Range>('all')
  const analytics = useAnalytics(range)
  const dashboard = useDashboard()
  const documents = useDocuments({ pageSize: 100 })

  const stats = dashboard.data
  const aData = analytics.data

  // Range-scoped document & signature metrics
  const isAll = range === 'all'
  const totalDocs = typeof aData?.totalDocuments === 'number'
    ? aData.totalDocuments
    : isAll ? (stats?.totalDocuments ?? 0) : 0
  const completedDocs = typeof aData?.completedDocuments === 'number'
    ? aData.completedDocuments
    : isAll ? (stats?.completedDocuments ?? 0) : 0
  const pendingDocs = typeof aData?.pendingDocuments === 'number'
    ? aData.pendingDocuments
    : isAll ? (stats?.pendingDocuments ?? 0) : 0
  const declinedDocs = typeof aData?.declinedDocuments === 'number'
    ? aData.declinedDocuments
    : isAll ? (stats?.declinedDocuments ?? 0) : 0
  const totalSigs = typeof aData?.totalSignatures === 'number'
    ? aData.totalSignatures
    : isAll ? (stats?.totalSignatures ?? 0) : 0
  const activeTenants = typeof aData?.activeTenants === 'number'
    ? aData.activeTenants
    : isAll ? (stats?.activeOrganizations ?? stats?.totalOrganizations ?? 0) : 0
  const activeUsers = typeof aData?.activeUsers === 'number'
    ? aData.activeUsers
    : isAll ? (stats?.activeUsers ?? stats?.totalUsers ?? 0) : 0

  const completionRate = totalDocs > 0 ? Math.round((completedDocs / totalDocs) * 100) : 0
  const pendingRate = totalDocs > 0 ? Math.round((pendingDocs / totalDocs) * 100) : 0
  const declinedRate = totalDocs > 0 ? Math.round((declinedDocs / totalDocs) * 100) : 0

  // Category breakdown strictly scoped to current range
  const categoryStats: CategoryStat[] = useMemo(() => {
    // If no documents exist in this range, category count must be empty
    if (totalDocs === 0) {
      return []
    }
    if (Array.isArray(aData?.categoryStats) && aData.categoryStats.length > 0) {
      return aData.categoryStats
    }
    if (isAll && Array.isArray(documents.data?.data) && documents.data.data.length > 0) {
      const map: Record<string, number> = {}
      for (const doc of documents.data.data) {
        const cat = doc.category || 'General / Standard'
        map[cat] = (map[cat] || 0) + 1
      }
      return Object.entries(map)
        .map(([name, count]) => ({
          name,
          count,
          pct: Math.round((count / (documents.data?.data?.length || 1)) * 100),
        }))
        .sort((a, b) => b.count - a.count)
    }
    return []
  }, [aData?.categoryStats, documents.data, totalDocs, isAll])

  // Total volume in current range
  const totalRangeVolume = useMemo(() => {
    if (!Array.isArray(analytics.data?.series)) return 0
    return analytics.data.series.reduce((sum: number, p: AnalyticsPoint) => sum + p.count, 0)
  }, [analytics.data])

  const peakDay = useMemo(() => {
    if (!Array.isArray(analytics.data?.series) || analytics.data.series.length === 0) return null
    return [...analytics.data.series].sort((a, b) => b.count - a.count)[0]
  }, [analytics.data])

  const rangeLabel = RANGES.find((r) => r.value === range)?.label.toLowerCase() || 'selected range'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-900 tracking-tight">Platform Analytics &amp; Intelligence</h1>
          <p className="mt-0.5 text-xs font-medium text-slate-500">
            Multi-tenant execution velocity, document throughput, and cryptographic signing volume.
          </p>
        </div>
        <div className="flex gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-xs">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              className={`rounded-lg px-3 py-1.5 font-mono text-xs font-bold transition-all ${
                range === r.value
                  ? 'bg-sky-600 text-white shadow-xs'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Primary KPI Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label="Total Documents"
          value={totalDocs}
          icon={<IconDocuments className="h-4 w-4" />}
          delta={range === '24h' ? `${stats?.documentsToday ?? 0} today` : `in ${rangeLabel}`}
          tone="neutral"
        />
        <StatCard
          label="Total Signatures"
          value={totalSigs}
          icon={<IconSignatures className="h-4 w-4" />}
          delta={range === '24h' ? `${stats?.signaturesToday ?? 0} today` : `in ${rangeLabel}`}
          tone="up"
        />
        <StatCard
          label="Completion Velocity"
          value={`${completionRate}%`}
          icon={<IconActivity className="h-4 w-4" />}
          delta={`${completedDocs} completed`}
          tone="up"
        />
        <StatCard
          label="Active Tenants"
          value={activeTenants}
          icon={<IconOrganizations className="h-4 w-4" />}
          delta={`${activeUsers} active users`}
          tone="neutral"
        />
      </div>

      {/* Main Activity Volume Chart */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">
              Platform Activity Volume Trend
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Cumulative operations (document creations, recipient dispatches, and signature events).
            </p>
          </div>
          <div className="flex items-center gap-4 text-xs font-mono">
            <div className="text-right">
              <span className="text-slate-400 block text-[10px] uppercase font-bold">Range Total</span>
              <span className="font-bold text-slate-900 text-sm">{totalRangeVolume} events</span>
            </div>
            {peakDay && peakDay.count > 0 && (
              <div className="text-right border-l border-slate-200 pl-4">
                <span className="text-slate-400 block text-[10px] uppercase font-bold">Peak Day</span>
                <span className="font-bold text-sky-700 text-xs">{peakDay.date} ({peakDay.count})</span>
              </div>
            )}
          </div>
        </div>

        {analytics.isLoading && <LoadingSkeleton rows={1} />}
        {analytics.isError && <ErrorState message="Could not load analytics data." onRetry={() => analytics.refetch()} />}
        {analytics.data && <SimpleChart points={analytics.data.series} />}
      </div>

      {/* 2-Column Insight Panels */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Document Execution Status Breakdown */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-xs">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-4">
            Document Execution Funnel
          </h2>

          <div className="space-y-3.5">
            <div>
              <div className="flex justify-between text-xs font-medium mb-1.5">
                <span className="flex items-center gap-1.5 text-slate-700 font-semibold">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
                  Completed &amp; Legally Executed
                </span>
                <span className="font-mono text-slate-900 font-bold">{completedDocs} ({completionRate}%)</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className="bg-emerald-500 h-2 rounded-full transition-all duration-300" style={{ width: `${completionRate}%` }} />
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs font-medium mb-1.5">
                <span className="flex items-center gap-1.5 text-slate-700 font-semibold">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" />
                  Pending &amp; In-Progress Signers
                </span>
                <span className="font-mono text-slate-900 font-bold">{pendingDocs} ({pendingRate}%)</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className="bg-amber-500 h-2 rounded-full transition-all duration-300" style={{ width: `${pendingRate}%` }} />
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs font-medium mb-1.5">
                <span className="flex items-center gap-1.5 text-slate-700 font-semibold">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-500 inline-block" />
                  Declined / Voided / Expired
                </span>
                <span className="font-mono text-slate-900 font-bold">{declinedDocs} ({declinedRate}%)</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className="bg-rose-500 h-2 rounded-full transition-all duration-300" style={{ width: `${declinedRate}%` }} />
              </div>
            </div>
          </div>

          <div className="mt-5 border-t border-slate-100 pt-3.5 flex justify-between items-center text-[11px] text-slate-500 font-mono">
            <span>Overall Execution Pipeline</span>
            <span className="text-emerald-800 font-bold bg-emerald-50 px-2.5 py-0.5 rounded-md border border-emerald-200">
              {completionRate >= 50 ? 'Healthy Velocity' : 'Active Pipeline'}
            </span>
          </div>
        </div>

        {/* Category Distribution Breakdown */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-xs">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-4">
            Document Category Distribution
          </h2>

          {analytics.isLoading ? (
            <div className="py-8 text-center text-xs font-mono text-slate-400">
              Loading category intelligence...
            </div>
          ) : categoryStats.length === 0 ? (
            <div className="py-8 text-center text-xs font-mono text-slate-400">
              No document activity recorded in {rangeLabel}.
            </div>
          ) : (
            <div className="space-y-3">
              {categoryStats.slice(0, 5).map((cat) => (
                <div key={cat.name}>
                  <div className="flex justify-between text-xs font-medium mb-1">
                    <span className="text-slate-800 font-bold truncate max-w-[240px]">{cat.name}</span>
                    <span className="font-mono text-slate-600 font-bold">{cat.count} ({cat.pct}%)</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5">
                    <div className="bg-sky-500 h-1.5 rounded-full" style={{ width: `${cat.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 border-t border-slate-100 pt-3.5 flex justify-between items-center text-[11px] text-slate-500 font-mono">
            <span>Cryptographic Integrity</span>
            <span className="text-sky-800 font-bold bg-sky-50 px-2.5 py-0.5 rounded-md border border-sky-200">
              100% SHA-256 Validated
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
